import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthImageSide from "./AuthImageSide";
import logo from "../../assets/images/isotipo-invecasa.svg";
import "./AuthPage.css";
import { resetPassword } from "../../api/authApi";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [form, setForm] = useState({
    password: "",
    confirm: ""
  });

  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const passwordMismatch = form.password && form.confirm && form.password !== form.confirm;

  // Apply consistent background style
  useEffect(() => {
    document.body.classList.add("authpage--background");
    return () => {
      document.body.classList.remove("authpage--background");
    };
  }, []);

  function validatePassword(password) {
    const errors = [];
    if (!/[A-Z]/.test(password)) errors.push("Password must include an uppercase letter");
    if (!/[a-z]/.test(password)) errors.push("Password must include a lowercase letter");
    if (!/\d/.test(password)) errors.push("Password must include a number");
    if (!/[@$!%*?&._-]/.test(password)) errors.push("Password must include a special character (@$!%*?&._-)");
    if (password.length < 8) errors.push("Password must be at least 8 characters long");
    return errors;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errorMessage) setErrorMessage("");
    if (name === "password") {
      setPasswordErrors(validatePassword(value));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.password || !form.confirm) {
      setErrorMessage("Please complete all fields.");
      return;
    }
    if (form.password !== form.confirm) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    const errors = validatePassword(form.password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      setErrorMessage("Password does not meet the requirements.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const res = await resetPassword(token, form.password);

      if (res && typeof res.json === "function") {
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setErrorMessage("");
          setSuccessMessage(data?.message || "Your password has been reset successfully.");
          setTimeout(() => navigate("/login"), 2200);
        } else {
          setSuccessMessage("");
          setErrorMessage(data?.error || data?.message || "An unexpected error occurred.");
        }
        return;
      }

      const status = res?.status ?? 200;
      const data = res?.data ?? res;
      if (status >= 200 && status < 300) {
        setErrorMessage("");
        setSuccessMessage(data?.message || "Your password has been reset successfully.");
        setTimeout(() => navigate("/login"), 2200);
      } else {
        setSuccessMessage("");
        setErrorMessage(data?.error || data?.message || "An unexpected error occurred.");
      }
    } catch (err) {
      setSuccessMessage("");
      const apiErr = err?.response?.data?.error || err?.message;
      setErrorMessage(apiErr || "An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="authpage">
      <AuthImageSide />
      <div className="authpage__form-container">
        <div className="authpage__card">
          <div className="authpage__logo">
            <img src={logo} alt="Logo" />
          </div>
          <h2 className="authpage__title">Reset your password</h2>
          <p className="authpage__subtitle">
            Enter and confirm your new password below.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="authpage__input-warning-wrap authpage__input-password-wrap">
              <input
                type={showPassword ? "text" : "password"}
                className={"authpage__input" + (form.password ? " filled" : "")}
                placeholder="New password"
                name="password"
                value={form.password}
                onChange={handleChange}
                autoComplete="new-password"
              />
              {form.password && (
                <button
                  type="button"
                  className="authpage__input-visibility-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {/* Eye icon */}
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 20 20"><path d="M1 10c2.5-4.5 6.5-7 9-7s6.5 2.5 9 7c-2.5 4.5-6.5 7-9 7s-6.5-2.5-9-7z" fill="none" stroke="#8493ad" strokeWidth="1.5"/><circle cx="10" cy="10" r="3" fill="none" stroke="#8493ad" strokeWidth="1.5"/></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20"><path d="M1 10c2.5-4.5 6.5-7 9-7s6.5 2.5 9 7c-2.5 4.5-6.5 7-9 7s-6.5-2.5-9-7z" fill="none" stroke="#8493ad" strokeWidth="1.5"/><circle cx="10" cy="10" r="3" fill="none" stroke="#8493ad" strokeWidth="1.5"/><line x1="5" y1="15" x2="15" y2="5" stroke="#8493ad" strokeWidth="1.5"/></svg>
                  )}
                </button>
              )}
            </div>

            <div className="authpage__input-warning-wrap authpage__input-password-wrap">
              <input
                type={showConfirm ? "text" : "password"}
                className={
                  "authpage__input" +
                  (form.confirm ? " filled" : "") +
                  (passwordMismatch ? " authpage__input--warning" : "")
                }
                placeholder="Confirm new password"
                name="confirm"
                value={form.confirm}
                onChange={handleChange}
                autoComplete="new-password"
              />
              {form.confirm && (
                <button
                  type="button"
                  className="authpage__input-visibility-btn"
                  onClick={() => setShowConfirm(!showConfirm)}
                  tabIndex={-1}
                >
                  {/* Eye icon */}
                  {showConfirm ? (
                    <svg width="20" height="20" viewBox="0 0 20 20"><path d="M1 10c2.5-4.5 6.5-7 9-7s6.5 2.5 9 7c-2.5 4.5-6.5 7-9 7s-6.5-2.5-9-7z" fill="none" stroke="#8493ad" strokeWidth="1.5"/><circle cx="10" cy="10" r="3" fill="none" stroke="#8493ad" strokeWidth="1.5"/></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20"><path d="M1 10c2.5-4.5 6.5-7 9-7s6.5 2.5 9 7c-2.5 4.5-6.5 7-9 7s-6.5-2.5-9-7z" fill="none" stroke="#8493ad" strokeWidth="1.5"/><circle cx="10" cy="10" r="3" fill="none" stroke="#8493ad" strokeWidth="1.5"/><line x1="5" y1="15" x2="15" y2="5" stroke="#8493ad" strokeWidth="1.5"/></svg>
                  )}
                </button>
              )}
            </div>

            {passwordErrors.length > 0 && (
              <ul style={{ color: "#facc15", fontSize: "0.85rem", marginTop: "8px", marginBottom: "8px" }}>
                {passwordErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}
            {errorMessage && (
              <p style={{ color: "#bdbdbd", fontSize: "0.9rem", marginBottom: "12px" }}>{errorMessage}</p>
            )}
            {successMessage && (
              <p style={{ color: "#4ade80", fontSize: "0.9rem", marginBottom: "12px" }}>{successMessage}</p>
            )}

            <button
              className="authpage__login-btn"
              type="submit"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Reset password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
